/**
 * Storefront Demo Page
 * 
 * This page demonstrates the dynamic content delivery hooks in action.
 * Use this to test:
 * - Ad serving from VIM slots
 * - Personalized product recommendations
 * - Video carousel with 1:3 auction ratio
 * - CDP tracking integration
 * 
 * NOTE: This is a test page. In production, these hooks would be integrated
 * into your actual storefront components.
 */

import React, { useState, useEffect } from 'react';
import { 
  LayoutGrid, Video, Image, ShoppingBag, Search, Eye, MousePointer,
  RefreshCw, Settings, Play, Pause, ChevronLeft, ChevronRight,
  Star, TrendingUp, User, Ban, AlertCircle, CheckCircle, Info
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

// Import storefront hooks
import {
  useAd,
  useRecommendations,
  useVideoCarousel,
  useCDPTracker,
  useAdPreferences,
  useCDPProfile
} from '../hooks/useStorefrontHooks';

// Import storefront config
import {
  ACTIVE_STORE,
  STORE_CONFIGS,
  getActiveStoreConfig,
  getAllStoreConfigs,
  getThemeConfig
} from '../config/storefrontConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== Ad Banner Component ====================
const AdBanner = ({ slotType, slotId, userId, className = '' }) => {
  const { adData, loading, error, trackClick, hasAd, theme, refetch } = useAd({
    slotType,
    slotId,
    userId,
    onAdLoad: (data) => console.log('Ad loaded:', data),
    onAdClick: (data) => console.log('Ad clicked:', data)
  });

  if (loading) {
    return (
      <div className={`animate-pulse bg-gray-200 rounded-lg h-48 flex items-center justify-center ${className}`}>
        <RefreshCw className="h-6 w-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error || !hasAd) {
    return (
      <div className={`bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg h-48 flex flex-col items-center justify-center ${className}`}>
        <Image className="h-8 w-8 text-gray-400 mb-2" />
        <p className="text-gray-500 text-sm">Ad Space: {slotId || slotType}</p>
        <p className="text-gray-400 text-xs mt-1">{error || 'No ad available'}</p>
        <Button variant="ghost" size="sm" className="mt-2" onClick={refetch}>
          <RefreshCw className="h-4 w-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div className={`relative group ${className}`}>
      <a 
        href={adData.destination_link || '#'}
        onClick={(e) => {
          e.preventDefault();
          trackClick();
          if (adData.destination_link) {
            window.open(adData.destination_link, '_blank');
          }
        }}
        className="block"
      >
        {/* Ad Creative */}
        <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg h-48 flex items-center justify-center text-white relative overflow-hidden">
          {adData.products?.[0]?.image_url ? (
            <img 
              src={adData.products[0].image_url} 
              alt={adData.products[0]?.name || 'Sponsored'} 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="text-center p-4">
              <ShoppingBag className="h-12 w-12 mx-auto mb-2 opacity-80" />
              <p className="font-semibold">{adData.campaign_name || 'Sponsored Content'}</p>
              {adData.products?.[0]?.name && (
                <p className="text-sm opacity-90">{adData.products[0].name}</p>
              )}
            </div>
          )}
          
          {/* Sponsored Badge */}
          <div className={`absolute top-2 right-2 ${theme.sponsoredBadgeColor} text-white text-xs px-2 py-1 rounded`}>
            {theme.sponsoredBadgeText}
          </div>
        </div>
      </a>
      
      {/* Debug Info (visible on hover) */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-white text-xs p-2 opacity-0 group-hover:opacity-100 transition-opacity rounded-b-lg">
        <div className="flex justify-between">
          <span>Campaign: {adData.campaign_id?.slice(0, 8)}...</span>
          <span>Slot: {adData.slot_id}</span>
        </div>
      </div>
    </div>
  );
};


// ==================== Product Card Component ====================
const ProductCard = ({ product, isSponsored, theme, onView }) => {
  return (
    <div 
      className={`border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer ${
        isSponsored ? 'ring-2 ring-amber-400' : ''
      }`}
      onClick={onView}
    >
      <div className="aspect-square bg-gray-100 relative">
        {product?.image_url ? (
          <img src={product.image_url} alt={product?.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag className="h-12 w-12 text-gray-300" />
          </div>
        )}
        {isSponsored && (
          <Badge className={`absolute top-2 left-2 ${theme.sponsoredBadgeColor} text-white`}>
            {theme.sponsoredBadgeText}
          </Badge>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-medium text-sm truncate">{product?.name || 'Product'}</h3>
        <div className="flex items-center justify-between mt-1">
          <span className="font-bold">₹{product?.price || '0.00'}</span>
          {product?.rating && (
            <div className="flex items-center text-xs text-gray-500">
              <Star className="h-3 w-3 text-amber-400 fill-amber-400 mr-1" />
              {product.rating}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ==================== Recommendation Grid Component ====================
const RecommendationGrid = ({ slotType, userId, limit = 8, title }) => {
  const { recommendations, loading, error, hasSponsored, theme, refetch } = useRecommendations({
    slotType,
    userId,
    limit
  });
  const { trackProductView } = useCDPTracker();

  const handleProductView = (item) => {
    if (item?.product_id) {
      trackProductView(item.product_id, item.product?.category_id, item.product?.vendor_id);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: limit }).map((_, i) => (
          <div key={`skeleton-${i}`} className="animate-pulse">
            <div className="bg-gray-200 aspect-square rounded-lg mb-2" />
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-1" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-gray-500">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p>Unable to load recommendations</p>
        <Button variant="ghost" size="sm" onClick={refetch} className="mt-2">
          <RefreshCw className="h-4 w-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div>
      {title && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">{title}</h3>
          {hasSponsored && (
            <Badge variant="outline" className="text-xs">Contains Sponsored</Badge>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {recommendations.map((item, idx) => (
          <ProductCard 
            key={item.product_id || idx}
            product={item.product}
            isSponsored={item.is_sponsored}
            theme={theme}
            onView={() => handleProductView(item)}
          />
        ))}
      </div>
    </div>
  );
};


// ==================== Video Carousel Component ====================
const VideoCarouselSection = ({ userId }) => {
  const { items, loading, error, activeIndex, setActiveIndex, trackClick, config, theme, refetch } = useVideoCarousel({
    userId
  });

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i.id || i.name || `${i.title || "i"}-${i}`} className="flex-shrink-0 w-48 animate-pulse">
            <div className="bg-gray-200 aspect-[4/5] rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  if (error || items.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg">
        <Video className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="text-gray-500">No video content available</p>
        <Button variant="ghost" size="sm" onClick={refetch} className="mt-2">
          <RefreshCw className="h-4 w-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Info about carousel config */}
      <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
        <Info className="h-4 w-4" />
        <span>Video Carousel: {config.auctionPositions.length} auction slot(s), {config.organicPositions.length} organic slot(s)</span>
      </div>
      
      {/* Carousel */}
      <div className="relative">
        <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
          {items.map((item, index) => {
            const isSponsored = item.is_sponsored;
            const videoUrl = item.product?.video_url || item.creative_url;
            
            return (
              <div 
                key={item.product_id || index}
                className={`flex-shrink-0 w-48 relative snap-start ${
                  isSponsored ? 'ring-2 ring-amber-400 rounded-lg' : ''
                }`}
                onClick={() => {
                  setActiveIndex(index);
                  trackClick(item);
                }}
              >
                {/* Slot type indicator */}
                <div className="absolute -top-2 left-2 z-10">
                  <Badge 
                    variant={isSponsored ? 'default' : 'outline'} 
                    className={`text-xs ${isSponsored ? theme.sponsoredBadgeColor : ''}`}
                  >
                    {item.slot_type || (isSponsored ? 'Auction' : 'Organic')}
                  </Badge>
                </div>
                
                {/* Video placeholder */}
                <div className="aspect-[4/5] bg-gradient-to-b from-gray-800 to-gray-900 rounded-lg flex items-center justify-center relative overflow-hidden">
                  {videoUrl ? (
                    <video
                      src={videoUrl}
                      autoPlay={index === activeIndex}
                      muted
                      loop
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-center text-white">
                      <Video className="h-8 w-8 mx-auto mb-2 opacity-60" />
                      <p className="text-xs opacity-60">No video</p>
                    </div>
                  )}
                  
                  {/* Play indicator */}
                  <div className="absolute bottom-2 right-2 bg-black/50 rounded-full p-1">
                    {index === activeIndex ? (
                      <Pause className="h-4 w-4 text-white" />
                    ) : (
                      <Play className="h-4 w-4 text-white" />
                    )}
                  </div>
                </div>
                
                {/* Product info */}
                {item.product?.name && (
                  <div className="mt-2">
                    <p className="text-sm font-medium truncate">{item.product.name}</p>
                    {item.product?.price && (
                      <p className="text-sm text-gray-600">₹{item.product.price}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Carousel indicators */}
        <div className="flex justify-center gap-2 mt-2">
          {items.map((item, index) => (
            <button
              key={item.id || item.name || `${item.title || "item"}-${index}`}
              onClick={() => setActiveIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === activeIndex 
                  ? (item.is_sponsored ? 'bg-amber-500' : 'bg-gray-800')
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};


// ==================== CDP Profile Viewer Component ====================
const CDPProfileViewer = ({ userId }) => {
  const { profile, loading, error, refetch } = useCDPProfile(userId);

  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        <div className="h-4 bg-gray-200 rounded w-1/2" />
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-4 bg-gray-200 rounded w-2/3" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="text-center py-4 text-gray-500">
        <User className="h-6 w-6 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">No profile data available</p>
        {userId && (
          <Button variant="ghost" size="sm" onClick={refetch} className="mt-2">
            <RefreshCw className="h-4 w-4 mr-1" /> Retry
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center gap-2">
        <User className="h-4 w-4 text-gray-400" />
        <span className="font-medium">{profile.user_id}</span>
      </div>
      
      {profile.viewed_categories?.length > 0 && (
        <div>
          <Label className="text-xs text-gray-500">Viewed Categories</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            {profile.viewed_categories.map((cat, i) => (
              <Badge key={cat.id || cat.name || `${cat.title || "cat"}-${i}`} variant="outline" className="text-xs">{cat}</Badge>
            ))}
          </div>
        </div>
      )}
      
      {profile.purchased_categories?.length > 0 && (
        <div>
          <Label className="text-xs text-gray-500">Purchased Categories</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            {profile.purchased_categories.map((cat, i) => (
              <Badge key={cat.id || cat.name || `${cat.title || "cat"}-${i}`} className="bg-green-100 text-green-800 text-xs">{cat}</Badge>
            ))}
          </div>
        </div>
      )}
      
      {profile.favorite_vendors?.length > 0 && (
        <div>
          <Label className="text-xs text-gray-500">Favorite Vendors</Label>
          <div className="flex flex-wrap gap-1 mt-1">
            {profile.favorite_vendors.map((v, i) => (
              <Badge key={v.id || v.name || `${v.title || "v"}-${i}`} className="bg-amber-100 text-amber-800 text-xs">{v}</Badge>
            ))}
          </div>
        </div>
      )}
      
      {profile.recent_views?.length > 0 && (
        <div>
          <Label className="text-xs text-gray-500">Recent Views ({profile.recent_views.length})</Label>
          <p className="text-xs text-gray-400 mt-1">
            Last viewed: {profile.recent_views[profile.recent_views.length - 1]?.product_id}
          </p>
        </div>
      )}
      
      <div className="pt-2 border-t">
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-gray-500">Total Purchases:</span>
            <span className="ml-1 font-medium">{profile.total_purchases || 0}</span>
          </div>
          <div>
            <span className="text-gray-500">Total Spent:</span>
            <span className="ml-1 font-medium">₹{(profile.total_spent || 0).toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};


// ==================== Main Storefront Demo Page ====================
const StorefrontDemo = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [testUserId, setTestUserId] = useState('test_user_123');
  const [testSlotId, setTestSlotId] = useState('HOME_BANNER_01');
  
  const config = getActiveStoreConfig();
  const theme = getThemeConfig();
  const { trackEvent, trackProductView, trackSearch, isEnabled } = useCDPTracker();
  
  // Test CDP tracking
  const handleTestTracking = async (eventType) => {
    const eventData = {
      view_product: { product_id: 'demo_prod_' + Date.now(), category_id: 'cat_demo', vendor_id: 'vendor_demo' },
      search: { query: 'test search', category_id: 'cat_demo', results_count: 10 },
      purchase: { category_id: 'cat_demo', amount: 99.99 },
      review: { vendor_id: 'vendor_demo', rating: 5 }
    };
    
    try {
      // Use direct API call for demo since we're using testUserId
      const response = await fetch(`${API_URL}/api/recommend/cdp/enrich`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: testUserId,
          event_type: eventType,
          event_data: eventData[eventType]
        })
      });
      const result = await response.json();
      
      toast({
        title: result.success ? 'Event Tracked!' : 'Tracking Failed',
        description: `Event: ${eventType}`,
        variant: result.success ? 'default' : 'destructive'
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive'
      });
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Storefront Demo</h1>
            <p className="text-gray-500 mt-1">Test dynamic content delivery hooks for your storefront</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={theme.sponsoredBadgeColor}>
              Active: {config.name}
            </Badge>
            <Badge variant="outline">{config.channel}</Badge>
          </div>
        </div>
        
        {/* Config Info */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-blue-900">How to Switch Stores</h3>
                <p className="text-sm text-blue-700 mt-1">
                  To switch between storefronts, update <code className="bg-blue-100 px-1 rounded">ACTIVE_STORE</code> in{' '}
                  <code className="bg-blue-100 px-1 rounded">/src/config/storefrontConfig.js</code>
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {getAllStoreConfigs().map(store => (
                    <Badge 
                      key={store.id} 
                      variant={store.id === ACTIVE_STORE ? 'default' : 'outline'}
                      className="text-xs"
                    >
                      {store.id === ACTIVE_STORE && <CheckCircle className="h-3 w-3 mr-1" />}
                      {store.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Test Controls */}
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Test Controls</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Test User ID</Label>
                <Input 
                  value={testUserId} 
                  onChange={(e) => setTestUserId(e.target.value)}
                  placeholder="User ID for testing"
                />
              </div>
              <div>
                <Label>Test Slot ID</Label>
                <Input 
                  value={testSlotId} 
                  onChange={(e) => setTestSlotId(e.target.value)}
                  placeholder="Slot ID for ad testing"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button variant="outline" onClick={() => setTestUserId('test_user_' + Date.now())}>
                  New User
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="ads">Ad Serving</TabsTrigger>
            <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
            <TabsTrigger value="video">Video Carousel</TabsTrigger>
            <TabsTrigger value="cdp">CDP Tracking</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Simulated Homepage */}
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Simulated Homepage</CardTitle>
                    <CardDescription>How ads and recommendations appear on your storefront</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Hero Banner Ad */}
                    <div>
                      <Label className="text-xs text-gray-500 mb-2 block">Hero Banner (Ad Slot)</Label>
                      <AdBanner slotId={testSlotId} userId={testUserId} />
                    </div>
                    
                    {/* Product Recommendations */}
                    <RecommendationGrid 
                      slotType="homepage" 
                      userId={testUserId} 
                      limit={4}
                      title="Recommended For You"
                    />
                    
                    {/* Video Carousel */}
                    <div>
                      <Label className="text-xs text-gray-500 mb-2 block">Video Carousel (1:3 Ratio)</Label>
                      <VideoCarouselSection userId={testUserId} />
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              {/* CDP Profile Sidebar */}
              <div>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">User CDP Profile</CardTitle>
                    <CardDescription>Data used for personalization</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <CDPProfileViewer userId={testUserId} />
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Ad Serving Tab */}
          <TabsContent value="ads" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Ad Serving Demo</CardTitle>
                <CardDescription>Test individual ad slots with different configurations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="mb-2 block">Home Banner</Label>
                    <AdBanner slotType="homeBanner" userId={testUserId} />
                  </div>
                  <div>
                    <Label className="mb-2 block">Category Banner</Label>
                    <AdBanner slotType="categoryBanner" userId={testUserId} />
                  </div>
                  <div>
                    <Label className="mb-2 block">Custom Slot ID</Label>
                    <AdBanner slotId={testSlotId} userId={testUserId} />
                  </div>
                  <div>
                    <Label className="mb-2 block">Search Sidebar</Label>
                    <AdBanner slotType="searchSidebar" userId={testUserId} className="h-64" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recommendations Tab */}
          <TabsContent value="recommendations" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Recommendation Engine Demo</CardTitle>
                <CardDescription>Personalized product recommendations with sponsored placements</CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <RecommendationGrid 
                  slotType="homepage" 
                  userId={testUserId} 
                  limit={8}
                  title="Homepage Recommendations"
                />
                <RecommendationGrid 
                  slotType="category" 
                  userId={testUserId} 
                  limit={8}
                  title="Category Page Recommendations"
                />
                <RecommendationGrid 
                  slotType="search" 
                  userId={testUserId} 
                  limit={8}
                  title="Search Results"
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Video Carousel Tab */}
          <TabsContent value="video" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Video Carousel Demo</CardTitle>
                <CardDescription>Instagram-style video carousel with 1:3 auction to organic ratio</CardDescription>
              </CardHeader>
              <CardContent>
                <VideoCarouselSection userId={testUserId} />
                
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium mb-2">Video Carousel Specifications</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Auction Slots:</span>
                      <span className="ml-1 font-medium">Position 0</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Organic Slots:</span>
                      <span className="ml-1 font-medium">Positions 1-3</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Aspect Ratio:</span>
                      <span className="ml-1 font-medium">4:5</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Max Duration:</span>
                      <span className="ml-1 font-medium">15 seconds</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CDP Tracking Tab */}
          <TabsContent value="cdp" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>CDP Event Tracking</CardTitle>
                  <CardDescription>Simulate user events to enrich CDP profile</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Button 
                      variant="outline" 
                      className="justify-start"
                      onClick={() => handleTestTracking('view_product')}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Track View
                    </Button>
                    <Button 
                      variant="outline" 
                      className="justify-start"
                      onClick={() => handleTestTracking('search')}
                    >
                      <Search className="h-4 w-4 mr-2" />
                      Track Search
                    </Button>
                    <Button 
                      variant="outline" 
                      className="justify-start"
                      onClick={() => handleTestTracking('purchase')}
                    >
                      <ShoppingBag className="h-4 w-4 mr-2" />
                      Track Purchase
                    </Button>
                    <Button 
                      variant="outline" 
                      className="justify-start"
                      onClick={() => handleTestTracking('review')}
                    >
                      <Star className="h-4 w-4 mr-2" />
                      Track Review
                    </Button>
                  </div>
                  
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h4 className="font-medium text-amber-900 mb-2">Event Types</h4>
                    <ul className="text-sm text-amber-800 space-y-1">
                      <li><strong>view_product:</strong> Updates viewed_categories, recent_views</li>
                      <li><strong>search:</strong> Updates search_history, interested_categories</li>
                      <li><strong>purchase:</strong> Updates purchased_categories, total_spent</li>
                      <li><strong>review:</strong> Updates favorite_vendors (if rating ≥ 4)</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>User Profile</CardTitle>
                  <CardDescription>Current CDP data for user: {testUserId}</CardDescription>
                </CardHeader>
                <CardContent>
                  <CDPProfileViewer userId={testUserId} />
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default StorefrontDemo;
